import AddIcon from "@atlaskit/icon/core/add";
import {useNavigate} from "@tanstack/react-router";
import {useRef} from "react";
import {MenuCustomDropdown} from "@vesta/aergia/components/dropdown";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeMenuButtonGroup} from "@vesta/ananke/components/anankeMenuButtonGroup";
import {AnankeMenuButtonItem} from "@vesta/ananke/components/anankeMenuButtonItem";
import {token} from "@atlaskit/tokens";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {DuluxRoles} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Users";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {bottomNavScaffoldIconBoxStyle} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/bottomNavIconBoxStyles";
import {configCtaDisplayLabel, renderConfigCtaIcon} from "./configCtaActionIcons";
import {
    findFormPageByFormId,
    isListStylePage,
    PageType,
    type Cta,
    type HeaderType,
    type WorkspaceConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {resolveConfigPageIdFromCta} from "./configPageIds";
import {hasWorkspaceUiConfig, loadOrCreateWorkspaceConfig} from "./configRuntime";
import {buildConfigScaffoldHref} from "~/workspaceConfig/scaffold/matchScaffoldActiveState";
import {useScaffoldPreviewNavigation} from "~/workspaceConfig/scaffoldTest/scaffoldPreviewNavigationContext";
import {argusClickConfigWithLocation, argusDomAttributes, toArgusHtmlDataAttributes} from "~/workspaceConfig/argusDomAttributes";
import {resolveScaffoldHeaderChromeArgus} from "~/workspaceConfig/scaffold/resolveScaffoldNavigation";

const DULUX_CREATE_CTA_IDS: ReadonlySet<string> = new Set([
    "cb754e77-8856-4f37-98bf-b8d2f9bc2b42",
    "382705a0-74b5-49a4-92ad-8e8ccb596056",
    "9906c4a6-7e5f-487b-90ba-49d4a0d0aafa",
    "a1b2c3d4-e5f6-4789-a012-3456789ab20d",
]);

const BLUE_STORE_CREATE_CTA_IDS: ReadonlySet<string> = new Set([
    "9a1b2c3d-4e5f-4678-9012-3456789abe01",
    "9a1b2c3d-4e5f-4678-9012-3456789abe02",
    "9a1b2c3d-4e5f-4678-9012-3456789abe03",
    "9a1b2c3d-4e5f-4678-9012-3456789abe04",
]);

const INVITE_USER_CTA_ID = "f1a2b3c4-d5e6-4789-a012-3456789abc06";
const INVITE_BLUESTORE_CTA_ID = "a8b9c0d1-e2f3-4567-8901-23456789ab31";

const DULUX_AID_ROLE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.RMM,
    DuluxRoles.MDO,
]);

const DULUX_FULL_CREATE_ROLE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.SystemAdministrator,
    DuluxRoles.HOCentral,
]);

const DULUX_BLUESTORE_ADMIN_ROLE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.SystemAdministrator,
    DuluxRoles.HOCentral,
    DuluxRoles.HOBlueStore,
]);

const DULUX_BLUESTORE_IDENTITY_EDIT_ROLE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.SystemAdministrator,
    DuluxRoles.HOCentral,
    DuluxRoles.HOBlueStore,
]);

const DULUX_STORE_CONFIGURATION_DENIED_ROLE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.BlueStoreOwner,
]);

function isDuluxUserTemplateId(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return (Object.values(DuluxRoles) as Array<string>).includes(userTemplateId);
}

export function isCreateCtaVisibleToUserTemplate(ctaId: string, userTemplateId: Uuid | undefined): boolean {
    if (ctaId === INVITE_BLUESTORE_CTA_ID) {
        return canAccessInviteBluestorePage(userTemplateId);
    }
    if (isDuluxUserTemplateId(userTemplateId) !== true || userTemplateId === undefined) {
        return true;
    }
    if (DULUX_FULL_CREATE_ROLE_IDS.has(userTemplateId) === true) {
        return true;
    }
    if (ctaId === INVITE_USER_CTA_ID) {
        return false;
    }
    if (DULUX_AID_ROLE_IDS.has(userTemplateId) === true) {
        return DULUX_CREATE_CTA_IDS.has(ctaId) === true;
    }
    return BLUE_STORE_CREATE_CTA_IDS.has(ctaId) === true;
}

export function canAccessInviteBluestorePage(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return DULUX_FULL_CREATE_ROLE_IDS.has(userTemplateId) === true;
}

export function canAccessBluestoreStoreConfiguration(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return DULUX_STORE_CONFIGURATION_DENIED_ROLE_IDS.has(userTemplateId) !== true;
}

export function canEditBluestoreStatus(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return DULUX_BLUESTORE_ADMIN_ROLE_IDS.has(userTemplateId) === true;
}

export function canEditBluestoreIdentity(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return DULUX_BLUESTORE_IDENTITY_EDIT_ROLE_IDS.has(userTemplateId) === true;
}

export function isBluestoreStoreOwner(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return false;
    }
    return DULUX_STORE_CONFIGURATION_DENIED_ROLE_IDS.has(userTemplateId) === true;
}

export function canEditBluestoreLocation(userTemplateId: Uuid | undefined): boolean {
    if (userTemplateId === undefined) {
        return true;
    }
    return isBluestoreStoreOwner(userTemplateId) !== true;
}

function resolveHeaderCreateMenuCtasUnfiltered(config: WorkspaceConfig): Array<Cta> {
    const pageToolbarCtaIds = new Set(
        Object.values(config.pages).flatMap((page) => {
            if (isListStylePage(page) !== true) {
                return [];
            }
            return page.viewEngine.toolbarCtaIds;
        }),
    );
    const createDropdowns = Object.values(config.ctas).filter((cta) => {
        return (
            cta.action.type === "dropdown" &&
            (cta.icon === "create" || cta.label.trim().toLowerCase() === "create")
        );
    });
    const createCta =
        createDropdowns.find((cta) => pageToolbarCtaIds.has(cta.id) !== true) ??
        createDropdowns[0];
    if (createCta === undefined || createCta.action.type !== "dropdown") {
        return [];
    }
    return createCta.action.ctaIds
        .map((ctaId) => config.ctas[ctaId])
        .filter((cta): cta is Cta => cta !== undefined);
}

export function isHeaderCreateFormAccessible(
    config: WorkspaceConfig,
    formId: Uuid,
    userTemplateId: Uuid | undefined,
): boolean {
    const matchingCreateCtas = resolveHeaderCreateMenuCtasUnfiltered(config).filter((cta) => {
        return cta.action.type === "openForm" && cta.action.formId === formId;
    });
    if (matchingCreateCtas.length === 0) {
        return true;
    }
    return matchingCreateCtas.some((cta) => isCreateCtaVisibleToUserTemplate(cta.id, userTemplateId) === true);
}

function resolveConfiguredHeaderCreateCtas(
    config: WorkspaceConfig,
    header: HeaderType | undefined,
): Array<Cta> | null {
    const createItem = header?.find((item) => item.type === "create");
    if (createItem === undefined || createItem.type !== "create") {
        return null;
    }
    if (createItem.ctaIds === undefined || createItem.ctaIds === null || createItem.ctaIds.length === 0) {
        return null;
    }
    return createItem.ctaIds
        .map((ctaId) => config.ctas[ctaId])
        .filter((cta): cta is Cta => cta !== undefined);
}

/**
 * Header Create menu items come from the role header `ctaIds` when that list is set.
 * Otherwise they come from the workspace Create dropdown CTA
 * (`icon: "create"` / label "Create" + `action.type: "dropdown"`), filtered by role.
 */
export function resolveHeaderCreateMenuCtas(
    config: WorkspaceConfig,
    userTemplateId?: Uuid,
): Array<Cta> {
    const header = userTemplateId === undefined ? undefined : config.scaffold[userTemplateId]?.header;
    const configured = resolveConfiguredHeaderCreateCtas(config, header);
    if (configured !== null) {
        return configured;
    }
    return resolveHeaderCreateMenuCtasUnfiltered(config).filter((cta) => {
        return isCreateCtaVisibleToUserTemplate(cta.id, userTemplateId) === true;
    });
}

/**
 * Mobile Create is only the CTAs explicitly placed on `mobileBottomCreate`.
 * Header Create (`icon: "create"` dropdown) must not appear on the bottom bar.
 */
export function resolveMobileBottomCreateCtas(
    config: WorkspaceConfig,
    userTemplateId?: Uuid,
): Array<Cta> {
    return Object.values(config.ctas).filter((cta) => {
        return (
            cta.placements !== null &&
            cta.placements.includes("mobileBottomCreate") === true &&
            isCreateCtaVisibleToUserTemplate(cta.id, userTemplateId) === true
        );
    });
}

export function resolveMobileBottomCreatePageTargets(
    workspaceId: Uuid,
    userTemplateId?: Uuid,
): Array<{
    id: Uuid;
    label: string;
    pageKey: Uuid;
    cta: Cta;
}> {
    if (hasWorkspaceUiConfig(workspaceId) !== true) {
        return [];
    }
    const config = loadOrCreateWorkspaceConfig(workspaceId);
    const targets: Array<{id: Uuid; label: string; pageKey: Uuid; cta: Cta}> = [];
    for (const cta of resolveMobileBottomCreateCtas(config, userTemplateId)) {
        const pageKey = resolveConfigPageIdFromCta(config, cta);
        if (pageKey === null) {
            continue;
        }
        targets.push({
            id: cta.id,
            label: cta.label,
            pageKey: pageKey as Uuid,
            cta: cta,
        });
    }
    return targets;
}

function resolveCreateMenuItemDescription(cta: Cta): string | undefined {
    const trimmed = cta.description?.trim();
    if (trimmed !== undefined && trimmed.length > 0) {
        return trimmed;
    }
    return undefined;
}

/** Header Create menu — items adapt per workspace config. */
export function ConfigHeaderCreateDropdown({workspaceId}: {workspaceId: Uuid}) {
    const navigate = useNavigate();
    const preview = useScaffoldPreviewNavigation();
    const {workspaceUser} = useWorkspaceUser();
    const closeRef = useRef<(() => void) | null>(null);
    if (hasWorkspaceUiConfig(workspaceId) !== true) {
        return null;
    }
    const config = loadOrCreateWorkspaceConfig(workspaceId);
    const createItems = resolveHeaderCreateMenuCtas(config, workspaceUser.templateId);
    const createTriggerArgus = resolveScaffoldHeaderChromeArgus({
        header: config.scaffold[workspaceUser.templateId]?.header ?? [],
        kind: "create",
    });

    if (createItems.length === 0) {
        return null;
    }

    return (
        <div className="mt-ds-space-050">
            <MenuCustomDropdown
                trigger={
                    <AnankeButton type="button" appearance="default" iconBefore={AddIcon} {...toArgusHtmlDataAttributes(argusDomAttributes(createTriggerArgus))}>
                        Create
                    </AnankeButton>
                }
                placement="bottom-end"
                closeRef={closeRef}
            >
                <AnankeMenuButtonGroup minWidth={320} maxHeight={480}>
                    {createItems.map((cta) => {
                        let pageKey: Uuid | null = null;
                        if (cta.action.type === "openForm") {
                            pageKey = findFormPageByFormId(config, cta.action.formId)?.id ?? null;
                        } else if (cta.action.type === "openPage") {
                            pageKey = cta.action.pageId;
                        }
                        const iconColor = token("color.icon.subtle");

                        return (
                            <AnankeMenuButtonItem
                                key={cta.id}
                                description={resolveCreateMenuItemDescription(cta)}
                                iconBefore={
                                    <div style={bottomNavScaffoldIconBoxStyle}>
                                        <span className="grid h-full w-full place-items-center text-ds-icon-subtle [&_svg]:size-[20px]">
                                            {renderConfigCtaIcon(cta, iconColor)}
                                        </span>
                                    </div>
                                }
                                {...argusDomAttributes(argusClickConfigWithLocation(cta.argus, "header"))}
                                onClick={() => {
                                    if (pageKey === null) {
                                        return;
                                    }
                                    const targetPage = config.pages[pageKey];
                                    const openCollectionCreate =
                                        targetPage !== undefined &&
                                        targetPage.type === PageType.Collections;
                                    if (preview !== null) {
                                        preview.onNavigate(
                                            buildConfigScaffoldHref({
                                                workspaceId: workspaceId,
                                                pageId: pageKey,
                                                view: openCollectionCreate === true ? "create" : undefined,
                                            }),
                                        );
                                    } else {
                                        void navigate({
                                            to: "/w/$workspaceId/$pageKey",
                                            params: {workspaceId, pageKey},
                                            search: {
                                                ...(openCollectionCreate === true
                                                    ? {view: "create"}
                                                    : {}),
                                            },
                                        });
                                    }
                                    closeRef.current?.();
                                }}
                            >
                                {configCtaDisplayLabel(cta)}
                            </AnankeMenuButtonItem>
                        );
                    })}
                </AnankeMenuButtonGroup>
            </MenuCustomDropdown>
        </div>
    );
}
